<!--
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
-->

<div class="sample-detail">
  <!-- Download Section -->
  <div class="download-section">
    <h3>Download Project Archive</h3>
    <p>A sample NodeJs + TypeScript backend demonstrating Service-to-Service authentication using Client Credentials Flow:</p>
    <a href="/samples/backend-typescript-nodejs/Athena.Auth.TypeScript.NodeJS.zip" class="download-btn" download>
      ⬇️ Athena.Auth.TypeScript.NodeJS.zip
    </a>
  </div>

  <!-- Overview Section -->
  <section class="section-overview">
    <h3>Project Overview</h3>
    <p>
      This sample shows how to implement <strong>OAuth 2.0 Client Credentials Flow</strong> using Azure AD to obtain
      access tokens for Athena API calls. It provides a simple backend that handles authentication and forwards requests
      to Athena's <strong>GCP (Generic Control Points)</strong> and <strong>TMS (Template Management Service)</strong> endpoints.
    </p>

    <h4>Application Features:</h4>
    <ul>
      <li>✓ <strong>NestJS 11.x</strong> - Modular Node.js framework with TypeScript</li>
      <li>✓ <strong>Node.js 22.x</strong> - JavaScript runtime for server-side development</li>
      <li>✓ <strong>TypeScript</strong> - Strongly typed programming language that builds on JavaScript</li>
      <li>✓ <strong>MSAL Node 3.x</strong> - Azure AD authentication with Client Credentials Flow</li>
      <li>✓ <strong>Zod Validation</strong> - Runtime validation for DTOs</li>
      <li>✓ <strong>neverthrow</strong> - Functional error handling with Result types</li>
      <li>✓ <strong>Automatic Token Management</strong> - Token caching and refresh handled automatically</li>
    </ul>

    <h4>Client Credentials Flow</h4>
    <p>
      <strong>Client Credentials Flow</strong> is an OAuth 2.0 authentication flow for service-to-service communication
      without user interaction.
    </p>
    <p><strong>How it works:</strong></p>
    <ol>
      <li>Backend authenticates to Azure AD using Client ID and Client Secret</li>
      <li>Azure AD returns an access token (valid ~1 hour)</li>
      <li>Backend includes token in requests to Athena API: <code>Authorization: Bearer &#123;token&#125;</code></li>
      <li>Token is automatically refreshed before expiration</li>
    </ol>
    <p>
      <strong>Key difference from user-based flows:</strong> No user login or consent required - the application authenticates as itself.
    </p>
  </section>

  <!-- Quick Start Section -->
  <section class="section-quickstart">
    <h3>Quick Start (3 Minutes)</h3>

    <i><strong>Note:</strong> Visual Studio Code can be used as IDE<br/></i>

    <h4>Prerequisites</h4>
    <p>Before you begin, ensure you have:</p>
    <ul>
      <li>Node.js 22.x (LTS)</li>
      <li>pnpm 9.x</li>
    </ul>

    <h4>Step-by-Step Setup</h4>

    <div class="subsection">
      <h5>1. Install Dependencies</h5>
      <div class="code-block">
        <pre><code># Install pnpm if needed
npm install -g pnpm&#64;9

# Install dependencies
pnpm install</code></pre>
      </div>
    </div>

    <div class="subsection">
      <h5>2. Configure Environment Variables</h5>
      <p>The sample includes a pre-configured <code>.env</code> file with placeholder values. <strong>All credentials must be provided by the Athena Support team.</strong></p>
      <p><strong>⚠️ Important:</strong> <b>The sample provides a default setup for the Dev environment.</b> If you need credentials for another environment (such as Prod), please contact <a href="mailto:Athena_Support@daacloud.onmicrosoft.com" title="">Athena_Support&#64;daacloud.onmicrosoft.com</a></p>
      <p>Required environment variables in <code>.env</code>:</p>
      <div class="code-block">
        <pre><code># Server
PORT=3000
NODE_ENV=development

# Azure AD (provided by Athena Support)
AZURE_TENANT_ID=your-tenant-id
AZURE_CLIENT_ID=your-client-id
AZURE_CLIENT_SECRET=your-client-secret

# Athena API (provided by Athena Support)
ATHENA_BASE_URL=https://ath-dataplane-cu-dev-apim.azure-api.net/api
ATHENA_SUBSCRIPTION_KEY=your-subscription-key
ATHENA_AUTH_ID=your-auth-id</code></pre>
      </div>
    </div>

    <div class="subsection">
      <h5>3. Run the Application</h5>
      <p><strong>Development Mode</strong> (with hot-reload):</p>
      <div class="code-block">
        <pre><code>pnpm start:dev</code></pre>
      </div>
      <p><strong>Production Build:</strong></p>
      <div class="code-block">
        <pre><code>pnpm build
pnpm start:prod</code></pre>
      </div>
      <p>The server starts at <code>http://localhost:3000</code></p>
    </div>
  </section>

  <!-- Testing APIs Section -->
  <section class="section-testing">
    <h3>Testing APIs</h3>
    <p>Once the server is running, you can test the API endpoints using any of the following methods:</p>

    <div class="subsection">
      <h4>Method 1: VS Code REST Client</h4>
      <p>The sample includes a <code>requests.http</code> file for testing with the VS Code REST Client extension.</p>

      <h5>Setup:</h5>
      <ol>
        <li>Open <code>requests.http</code> in VS Code</li>
        <li>Install <a href="https://marketplace.visualstudio.com/items?itemName=humao.rest-client" target="_blank" rel="noopener noreferrer">REST Client extension</a></li>
        <li>Click "Send Request" above any request</li>
      </ol>
    </div>

    <div class="subsection">
      <h4>Method 2: Postman</h4>
      <p>Use Postman to create a collection and test all endpoints.</p>

      <h5>Setup:</h5>
      <ol>
        <li>Create a new collection in Postman</li>
        <li>Add requests for each endpoint as shown below</li>
      </ol>

      <h5>Available Endpoints:</h5>

      <p><strong>Get all products:</strong></p>
      <ul>
        <li>Method: <code>GET</code></li>
        <li>URL: <code>http://localhost:3000/api/athena/products</code></li>
      </ul>

      <p><strong>Get specific product:</strong></p>
      <ul>
        <li>Method: <code>GET</code></li>
        <li>URL: <code>http://localhost:3000/api/athena/product?name=AGZ</code></li>
      </ul>

      <p><strong>Get product versions list:</strong></p>
      <ul>
        <li>Method: <code>GET</code></li>
        <li>URL: <code>http://localhost:3000/api/athena/product-versions/list</code></li>
      </ul>

      <p><strong>Get specific product version:</strong></p>
      <ul>
        <li>Method: <code>GET</code></li>
        <li>URL: <code>http://localhost:3000/api/athena/product-version/WTC/Climatix/1</code></li>
      </ul>

      <p><strong>Generate Niagara template:</strong></p>
      <ul>
        <li>Method: <code>GET</code></li>
        <li>URL: <code>http://localhost:3000/api/athena/templates/niagara</code></li>
      </ul>
    </div>

    <div class="subsection">
      <h4>Method 3: curl</h4>
      <p>Use curl commands from terminal or command line.</p>

      <h5>Example Commands:</h5>
      <div class="code-block">
        <pre><code># Get all products
curl http://localhost:3000/api/athena/products

# Get specific product
curl "http://localhost:3000/api/athena/product?name=AGZ"

# Get product versions list
curl "http://localhost:3000/api/athena/product-versions/list"

# Get specific product version
curl http://localhost:3000/api/athena/product-version/WTC/Climatix/1

# Generate Niagara template
curl http://localhost:3000/api/athena/templates/niagara</code></pre>
      </div>
    </div>
  </section>

  <!-- Project Structure Section -->
  <section class="section-structure">
    <h3>Project Structure</h3>
    <div class="code-block-light">
      <pre><code>src/
├── main.ts                      # Application entry point
├── app.module.ts                # Root application module
├── config/                      # Configuration management
│   ├── configuration.ts         # Environment configuration factory
│   ├── configuration.service.ts # Typed configuration service
│   ├── configuration.module.ts  # Configuration module
│   └── validation.schema.ts     # Zod validation schemas
├── auth/                        # Authentication module
│   ├── auth.module.ts
│   └── auth.service.ts          # MSAL integration with token management
├── athena/                      # Athena API module
│   ├── athena.module.ts
│   ├── athena.controller.ts     # REST API endpoints
│   ├── athena.service.ts        # Business logic with error handling
│   └── dto/
│       └── athena.dto.ts        # Request/response DTOs with Zod
└── common/                      # Shared utilities</code></pre>
    </div>
  </section>

  <!-- Core Services Section -->
  <section class="section-services">
    <h3>Core Modules</h3>

    <!-- Auth Module -->
    <div class="service-card">
      <h4>1. Authentication Module (src/auth/)</h4>
      <p>Handles Azure AD authentication using MSAL with Client Credentials Flow:</p>

      <h5>Key Features:</h5>
      <ul>
        <li><strong>MSAL Confidential Client:</strong> OAuth 2.0 Client Credentials Flow</li>
        <li><strong>Automatic Token Management:</strong> Token acquisition, caching, and refresh</li>
        <li><strong>Token Expiration Handling:</strong> Automatic refresh before expiry</li>
      </ul>
    </div>

    <!-- Athena Module -->
    <div class="service-card">
      <h4>2. Athena Module (src/athena/)</h4>
      <p>Provides REST API endpoints and business logic for Athena API integration:</p>

      <h5>Components:</h5>
      <ul>
        <li><strong>athena.controller.ts:</strong> REST API endpoints for the current sample project</li>
        <li><strong>athena.service.ts:</strong> <b> The main class demonstrating how to send requests through Athena</b></li>
      </ul>

      <h5>Supported Services:</h5>
      <ul>
        <li><strong>GCP (Generic Control Points):</strong> Products and product versions</li>
        <li><strong>TMS (Template Management Service):</strong> Niagara template generation</li>
      </ul>
    </div>
  </section>

  <!-- Client Credentials Flow Section -->
  <section class="section-authorization">
    <h3>How Client Credentials Flow Works</h3>

    <div class="subsection">
      <h4>Authentication Flow</h4>

      <h5>MSAL Implementation Details</h5>
      <p>This sample uses <strong>&#64;azure/msal-node</strong> library for Azure AD authentication.</p>

      <h5>1. Initialize MSAL Confidential Client</h5>
      <div class="code-block" ngNonBindable>
        <pre><code>// Initialize MSAL client
const msalConfig = &#123;
  auth: &#123;
    clientId: config.clientId,
    authority: `https://login.microsoftonline.com/$&#123;config.tenantId&#125;`,
    clientSecret: config.clientSecret,
  &#125;,
&#125;;
const msalClient = new ConfidentialClientApplication(msalConfig);</code></pre>
      </div>

      <h5>2. Acquire Token</h5>
      <div class="code-block" ngNonBindable>
        <pre><code>// Acquire token
const tokenRequest = &#123;
  scopes: [`https://&#123;tenant&#125;.onmicrosoft.com/&#123;app-id&#125;/.default`],
&#125;;
const response = await msalClient.acquireTokenByClientCredential(tokenRequest);
const accessToken = response.accessToken;</code></pre>
      </div>

      <h5>3. Use Token in API Request</h5>
      <div class="code-block" ngNonBindable>
        <pre><code>// Use token in API request
axios.get(athenaUrl, &#123;
  headers: &#123;
    Authorization: `Bearer $&#123;accessToken&#125;`,
    'X-Subscription-Key': subscriptionKey,
  &#125;,
&#125;);</code></pre>
      </div>

      <h5>Key Implementation Details:</h5>
      <ul>
        <li><strong>Token Acquisition:</strong> Uses <code>ConfidentialClientApplication.acquireTokenByClientCredential()</code></li>
        <li><strong>Token Caching:</strong> MSAL handles token caching automatically</li>
        <li><strong>Token Refresh:</strong> Tokens are refreshed automatically before expiration</li>
        <li><strong>Scope:</strong> <code>https://&#123;tenant&#125;.onmicrosoft.com/&#123;app-id&#125;/.default</code></li>
      </ul>
    </div>

    <div class="subsection">
      <h4>Difference from Authorization Code Flow</h4>
      <p>Unlike the frontend samples that use <strong>Authorization Code Flow</strong> with user interaction,
      this backend sample uses <strong>Client Credentials Flow</strong>:</p>

      <ul>
        <li><strong>No User Interaction:</strong> Designed for service-to-service (S2S) communication</li>
        <li><strong>Application Permissions:</strong> Uses application-level permissions, not delegated user permissions</li>
        <li><strong>Client Secret Required:</strong> Requires a confidential client secret (never exposed to frontend)</li>
        <li><strong>Direct Token Request:</strong> No redirect flows or user consent required</li>
        <li><strong>Backend Only:</strong> Should only be used in secure backend environments</li>
      </ul>
    </div>
  </section>

  <!-- Troubleshooting Section -->
  <section class="section-troubleshooting">
    <h3>Troubleshooting</h3>

    <h4>Port Already in Use</h4>
    <p>If port 3000 is already in use, stop the process using PowerShell:</p>
    <div class="code-block">
      <pre><code>Get-NetTCPConnection -LocalPort 3000 -ErrorAction SilentlyContinue | Select-Object -ExpandProperty OwningProcess | ForEach-Object &#123; Stop-Process -Id $_ -Force &#125;</code></pre>
    </div>
    <p>Or use a different port:</p>
    <div class="code-block">
      <pre><code>PORT=3001 pnpm start:dev</code></pre>
    </div>
  </section>

  <!-- Support Section -->
  <section class="section-support">
    <h3>Support</h3>
    <p>For questions about:</p>
    <ul>
      <li><strong>Credentials & Configuration:</strong> Contact Athena Support team</li>
      <li><strong>Azure AD Setup:</strong> Contact your Azure administrator</li>
      <li><strong>Code Issues:</strong> Check application logs with <code>pnpm start:dev</code></li>
    </ul>
    <p>
      For additional support, refer to the <a href="/api-mesh/samples#getting-started">API Mesh documentation</a>
      or contact the Athena Support team at <a href="mailto:Athena_Support&#64;daacloud.onmicrosoft.com">Athena_Support&#64;daacloud.onmicrosoft.com</a>.
    </p>
  </section>
</div>
